import { useEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import type { GridColumn } from './types';
import { formatValue, parseDateInput, parseNumberInput, formatDate, formatTime } from '@/lib/format';
import { LookupDialog } from './LookupDialog';
import { toast } from 'sonner';

/** Satır içi düzenleme hücresi: kolon türüne göre uygun editör */
export function CellEditor({ col, row, onChange, autoFocus }: {
  col: GridColumn;
  row: Record<string, any>;
  onChange: (patch: Record<string, any>) => void;
  autoFocus?: boolean;
}) {
  const value = row[col.key];
  const [lookupOpen, setLookupOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);

  if (col.readonly || col.editor === 'readonly') {
    return <div className="truncate px-1.5 muted">{col.display ? col.display(row) : formatValue(value, col.sqlType, col.mask)}</div>;
  }
  const common = 'h-full w-full border-0 bg-transparent px-1.5 outline-none focus:bg-brand-50 dark:focus:bg-brand-900/40';

  switch (col.editor) {
    case 'check':
      return (
        <div className="flex h-full items-center justify-center">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange({ [col.key]: e.target.checked })} />
        </div>
      );
    case 'number':
      return <NumberInput inputRef={ref} className={common + ' text-right'} value={value} onChange={(v) => onChange({ [col.key]: v })} />;
    case 'date':
    case 'datetime':
      return <TextLikeInput inputRef={ref} className={common} value={value == null ? '' : col.editor === 'date' ? formatDate(value, false) : formatDate(value, true)}
        placeholder="gg.aa.yyyy" onCommit={(s) => onChange({ [col.key]: parseDateInput(s) })} />;
    case 'time':
      return <TextLikeInput inputRef={ref} className={common} value={value == null ? '' : formatTime(value)} placeholder="ss:dd:ss"
        onCommit={(s) => onChange({ [col.key]: s ? `1899-12-30T${s.length === 5 ? s + ':00' : s}` : null })} />;
    case 'combo':
      return <ComboInput col={col} inputRef={ref} className={common} value={value} onChange={(v) => onChange({ [col.key]: v })} />;
    case 'lookup':
      return (
        <div className="flex h-full items-stretch">
          <TextLikeInput inputRef={ref} className={common} value={value == null ? '' : String(value)}
            onCommit={async (s) => {
              const patch: Record<string, any> = { [col.key]: s || null };
              if (s && col.lookup?.validate) {
                try {
                  const r = await col.lookup.validate(s, { ...row, ...patch });
                  if (!r.ok) toast.warning(r.message ?? 'Değer listede bulunamadı');
                  Object.assign(patch, r.values);
                } catch (e: any) {
                  toast.error(e.message);
                }
              }
              onChange(patch);
            }} />
          {col.lookup && (
            <button type="button" className="px-1 muted hover:text-[var(--text)]" title="Listeden seç (F4)" onClick={() => setLookupOpen(true)}><MoreHorizontal size={14} /></button>
          )}
          {col.lookup && lookupOpen && (
            <LookupDialog spec={col.lookup} row={row} open={lookupOpen} onClose={() => setLookupOpen(false)} onPick={(v) => onChange(v)} />
          )}
        </div>
      );
    case 'memo':
      return <TextLikeInput inputRef={ref} className={common} value={value ?? ''} onCommit={(s) => onChange({ [col.key]: s })} />;
    default:
      return <TextLikeInput inputRef={ref} className={common} value={value ?? ''} maxLength={col.maxLength} onCommit={(s) => onChange({ [col.key]: s })} />;
  }
}

function TextLikeInput({ value, onCommit, className, placeholder, maxLength, inputRef, list }: {
  value: string; onCommit: (s: string) => void; className: string; placeholder?: string; maxLength?: number; inputRef?: React.RefObject<HTMLInputElement | null>; list?: string;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input ref={inputRef} className={className} value={v} placeholder={placeholder} maxLength={maxLength} list={list}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v !== value && onCommit(v)}
      onKeyDown={(e) => { if (e.key === 'Enter' && v !== value) onCommit(v); }} />
  );
}

function NumberInput({ value, onChange, className, inputRef }: { value: unknown; onChange: (v: number | null) => void; className: string; inputRef?: React.RefObject<HTMLInputElement | null> }) {
  const shown = value == null || value === '' ? '' : String(value).replace('.', ',');
  return <TextLikeInput inputRef={inputRef} className={className} value={shown} onCommit={(s) => {
    const n = parseNumberInput(s);
    if (s.trim() && n == null) { toast.error('Geçerli bir sayı giriniz'); return; }
    onChange(n);
  }} />;
}

function ComboInput({ col, value, onChange, className, inputRef }: { col: GridColumn; value: unknown; onChange: (v: unknown) => void; className: string; inputRef?: React.RefObject<HTMLInputElement | null> }) {
  const [items, setItems] = useState(col.comboItems ?? []);
  useEffect(() => {
    if (col.loadComboItems) col.loadComboItems().then(setItems).catch(() => setItems([]));
  }, [col]);
  const id = useMemo(() => `cmb-${Math.random().toString(36).slice(2)}`, []);
  const objectItems = items.some((i) => typeof i === 'object');
  if (objectItems) {
    return (
      <select className={className} value={value == null ? '' : String(value)} onChange={(e) => {
        const it = (items as { value: unknown; label: string }[]).find((x) => String(x.value) === e.target.value);
        onChange(it ? it.value : null);
      }}>
        <option value="" />
        {(items as { value: unknown; label: string }[]).map((i) => <option key={String(i.value)} value={String(i.value)}>{i.label}</option>)}
      </select>
    );
  }
  return (
    <>
      <TextLikeInput inputRef={inputRef} list={id} className={className} value={value == null ? '' : String(value)} onCommit={(s) => onChange(s)} />
      <datalist id={id}>{(items as string[]).map((i) => <option key={i} value={i} />)}</datalist>
    </>
  );
}
